/**
 * User Festival Status Types and Utilities
 * 
 * Handles the social communication layer for festival attendance status.
 * Users can signal their festival plans to friends using casual language.
 */

/**
 * User festival status options
 * These represent social signals, separate from detailed set planning
 */
export const USER_FESTIVAL_STATUS = {
	WANT_TO_GO: "want_to_go", // "I want to go"
	GOING: "going", // "I'm going"
	WENT: "went", // "I went"
	WATCHED_ONLINE: "watched_online", // "I saw stream"
} as const;

export type UserFestivalStatus =
	| (typeof USER_FESTIVAL_STATUS)[keyof typeof USER_FESTIVAL_STATUS]
	| null;

/**
 * Legacy status values for migration
 */
export const LEGACY_STATUS = {
	GOING_LIVE: "going_live",
	GOING_ONLINE: "going_online",
} as const;

/**
 * Get display label for status
 * @param status - The user's festival status
 * @param isPast - Whether the festival has already happened
 * @returns Display text for the status
 */
export function getStatusLabel(
	status: UserFestivalStatus,
	isPast: boolean = false,
): string {
	switch (status) {
		case USER_FESTIVAL_STATUS.WANT_TO_GO:
			return isPast ? "Wanted to Go" : "Want to Go";
		case USER_FESTIVAL_STATUS.GOING:
			return isPast ? "Went" : "Going"; // Auto-transition display
		case USER_FESTIVAL_STATUS.WENT:
			return "Went";
		case USER_FESTIVAL_STATUS.WATCHED_ONLINE:
			return "Saw Stream";
		default:
			// For null/undefined status
			// Note: Platforms may override this for their specific UX
			// Web typically shows "Want to Go" as a call-to-action
			// Mobile typically shows "Add Status" as descriptive text
			return "Add Status";
	}
}

/**
 * Icon configuration for status
 */
export interface StatusIconConfig {
	name: string; // Icon name (platform-specific)
	filled: boolean;
}

/**
 * Get icon configuration for status
 * Returns generic icon names that can be mapped to platform-specific icons
 * @param status - The user's festival status
 * @returns Icon configuration
 */
export function getStatusIconConfig(status: UserFestivalStatus): StatusIconConfig {
	switch (status) {
		case USER_FESTIVAL_STATUS.WANT_TO_GO:
			return { name: "pin", filled: true };
		case USER_FESTIVAL_STATUS.GOING:
		case USER_FESTIVAL_STATUS.WENT:
			return { name: "calendar-check", filled: true };
		case USER_FESTIVAL_STATUS.WATCHED_ONLINE:
			return { name: "live", filled: true };
		default:
			return { name: "pin", filled: false };
	}
}

/**
 * Get color value for status
 * Returns hex colors that work across platforms
 * @param status - The user's festival status
 * @returns Hex color string
 */
export function getStatusColor(status: UserFestivalStatus): string {
	switch (status) {
		case USER_FESTIVAL_STATUS.WANT_TO_GO:
			return "#3b82f6";
		case USER_FESTIVAL_STATUS.GOING:
			return "#10b981";
		case USER_FESTIVAL_STATUS.WENT:
			return "#10b981";
		case USER_FESTIVAL_STATUS.WATCHED_ONLINE:
			return "#e11d48";
		default:
			return "#6b7280";
	}
}

/**
 * Determine valid status options for a festival
 * @param isPast - Whether the festival has already happened
 * @returns Array of valid status options
 */
export function getValidStatusOptions(isPast: boolean): UserFestivalStatus[] {
	if (isPast) {
		return [
			USER_FESTIVAL_STATUS.WANT_TO_GO,
			USER_FESTIVAL_STATUS.WENT,
			USER_FESTIVAL_STATUS.WATCHED_ONLINE,
		];
	}
	return [USER_FESTIVAL_STATUS.WANT_TO_GO, USER_FESTIVAL_STATUS.GOING];
}

/**
 * Determine valid status transitions
 * @param currentStatus - Current status
 * @param festivalIsPast - Whether the festival has passed
 * @returns Array of valid next statuses
 */
export function getValidTransitions(
	currentStatus: UserFestivalStatus,
	festivalIsPast: boolean,
): UserFestivalStatus[] {
	if (festivalIsPast) {
		// Past festivals
		if (currentStatus === null)
			return [USER_FESTIVAL_STATUS.WENT, USER_FESTIVAL_STATUS.WATCHED_ONLINE];
		if (currentStatus === USER_FESTIVAL_STATUS.WENT)
			return [USER_FESTIVAL_STATUS.WATCHED_ONLINE, null];
		if (currentStatus === USER_FESTIVAL_STATUS.WATCHED_ONLINE)
			return [USER_FESTIVAL_STATUS.WENT, null];
		// Handle other cases
		return [
			USER_FESTIVAL_STATUS.WENT,
			USER_FESTIVAL_STATUS.WATCHED_ONLINE,
			null,
		];
	} else {
		// Future festivals
		if (currentStatus === null)
			return [USER_FESTIVAL_STATUS.WANT_TO_GO, USER_FESTIVAL_STATUS.GOING];
		if (currentStatus === USER_FESTIVAL_STATUS.WANT_TO_GO)
			return [USER_FESTIVAL_STATUS.GOING, null];
		if (currentStatus === USER_FESTIVAL_STATUS.GOING)
			return [USER_FESTIVAL_STATUS.WANT_TO_GO, null];
		return [null];
	}
}

/**
 * Get next status in cycle
 * @param currentStatus - Current status
 * @param festivalIsPast - Whether the festival has passed
 * @returns Next status in the cycle
 */
export function getNextStatus(
	currentStatus: UserFestivalStatus,
	festivalIsPast: boolean,
): UserFestivalStatus {
	if (festivalIsPast) {
		// Past festival cycle: null → went → watched_online → null
		switch (currentStatus) {
			case null:
				return USER_FESTIVAL_STATUS.WENT;
			case USER_FESTIVAL_STATUS.WENT:
				return USER_FESTIVAL_STATUS.WATCHED_ONLINE;
			case USER_FESTIVAL_STATUS.WATCHED_ONLINE:
				return null;
			default:
				return null;
		}
	}

	// Future festival cycle: null → want_to_go → going → null
	switch (currentStatus) {
		case null:
			return USER_FESTIVAL_STATUS.WANT_TO_GO;
		case USER_FESTIVAL_STATUS.WANT_TO_GO:
			return USER_FESTIVAL_STATUS.GOING;
		case USER_FESTIVAL_STATUS.GOING:
			return null;
		default:
			return null;
	}
}

/**
 * Should auto-transition status (going → went when festival ends)
 * @param status - Current status
 * @param festivalEndDate - Festival end date
 * @returns Whether status should auto-transition
 */
export function shouldAutoTransition(
	status: UserFestivalStatus,
	festivalEndDate: string | Date,
): boolean {
	if (status !== USER_FESTIVAL_STATUS.GOING) return false;

	const endDate = new Date(festivalEndDate);
	const now = new Date();
	return endDate < now;
}

/**
 * Map legacy status to new status
 * @param legacyStatus - Legacy status value
 * @returns Mapped status value
 */
export function mapLegacyStatus(legacyStatus: string): UserFestivalStatus {
	switch (legacyStatus) {
		case LEGACY_STATUS.GOING_LIVE:
		case LEGACY_STATUS.GOING_ONLINE:
			return USER_FESTIVAL_STATUS.GOING;
		default:
			return legacyStatus as UserFestivalStatus;
	}
}